import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../utils/axiosInstance';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { FiActivity, FiCheckCircle, FiEye, FiEyeOff, FiFileText, FiLock, FiMail, FiShield } from 'react-icons/fi';
import { FcGoogle } from 'react-icons/fc';
import { Button } from '../../components/ui/Button';
import { FormField } from '../../components/ui/FormField';
import { ThemeToggle } from '../../components/AppShell/ThemeToggle';
import { AppTooltip } from '../../components/ui/AppTooltip';

export const Login = () => {
  const { t } = useTranslation();
  const router = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const completeLogin = () => {
    router('/dashboard');
  };

  const validate = ({ email, password }) => {
    const errors = {};
    if (!email.trim()) errors.email = t('Email is required');
    else if (!/^\S+@\S+\.\S+$/.test(email)) errors.email = t('Enter a valid email address');
    if (!password) errors.password = t('Password is required');
    return errors;
  };

  const validateField = (event) => {
    const { name, value, form } = event.target;
    const errors = validate({
      email: name === 'email' ? value : form.elements.email.value,
      password: name === 'password' ? value : form.elements.password.value,
    });
    setFieldErrors((current) => ({ ...current, [name]: errors[name] }));
  };

  const clearFieldError = (event) => {
    const { name } = event.target;
    if (fieldErrors[name]) setFieldErrors((current) => ({ ...current, [name]: undefined }));
  };

  const onSubmit = async (event) => {
    event.preventDefault();

    if (isSubmitting) return;

    const form = event.currentTarget;
    const credentials = {
      email: form.elements.email.value,
      password: form.elements.password.value,
    };
    const errors = validate(credentials);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');

    try {
      const response = await axiosInstance('/user/login', 'post', {
        email: credentials.email,
        password: credentials.password,
      });

      if (response.status === 200) {
        if (response.data?.requiresTwoFactor) {
          window.sessionStorage.setItem('twoFactorChallenge', response.data.challengeToken);
          window.sessionStorage.setItem('twoFactorAction', response.data.action || 'verify');
          router('/two-factor', { replace: true });
          return;
        }
        toast.success(t('Login successful'));
        completeLogin();
      } else {
        const message = t('Invalid email or password');
        setErrorMessage(message);
        toast.error(message);
      }
    } catch (err) {
      const message = t('Unable to sign in right now');
      setErrorMessage(message);
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className='login-screen grid h-[100dvh] overflow-hidden lg:grid-cols-[60%_40%]'>
      <section className='login-visual relative hidden overflow-hidden p-10 text-white lg:flex lg:flex-col xl:p-12' aria-hidden='true'>
        <div className='relative z-10 flex items-center gap-3'>
          <span className='flex h-12 w-12 items-center justify-center rounded-lg border border-white/15 bg-white/10'><FiShield aria-hidden='true' /></span>
          <div><span className='block text-xl font-bold'>{t('Billing Management')}</span><span className='mt-0.5 block text-xs font-semibold tracking-[0.12em] text-cyan-200'>{t('Financial operations')}</span></div>
        </div>
        <div className='relative z-10 mx-auto flex w-full max-w-[760px] flex-1 flex-col justify-center py-12'>
          <div className='login-flow-canvas'>
            <div className='login-document login-document-primary'><div className='flex items-center justify-between'><FiFileText className='text-cyan-200' aria-hidden='true' /><FiCheckCircle className='text-emerald-300' aria-hidden='true' /></div><div className='mt-5 h-2 w-3/4 rounded-full bg-white/70' /><div className='mt-3 h-2 w-full rounded-full bg-white/20' /><div className='mt-2 h-2 w-5/6 rounded-full bg-white/20' /></div>
            <div className='login-document login-document-secondary'><FiActivity className='text-cyan-200' aria-hidden='true' /><div className='mt-5 flex h-16 items-end gap-2'>{[35, 62, 48, 76, 58].map((height) => <span key={height} className='flex-1 rounded-t-sm bg-cyan-200/65' style={{ height: `${height}%` }} />)}</div></div>
            <div className='login-flow-node login-flow-node-a' /><div className='login-flow-node login-flow-node-b' /><div className='login-flow-node login-flow-node-c' />
            <span className='login-flow-line login-flow-line-a' /><span className='login-flow-line login-flow-line-b' /><span className='login-flow-line login-flow-line-c' />
            <span className='login-flow-pulse login-flow-pulse-a' /><span className='login-flow-pulse login-flow-pulse-b' />
          </div>
          <div className='mt-12 max-w-2xl'><p className='text-sm font-semibold uppercase tracking-[0.16em] text-cyan-200'>{t('Financial operations')}</p><h1 className='mt-4 text-4xl font-bold leading-tight xl:text-5xl'>{t('Control invoices, payments, and approvals with confidence')}</h1><p className='mt-5 max-w-xl text-base leading-7 text-indigo-100'>{t('Secure access to your billing workspace with real-time approval flows and customer balances.')}</p></div>
        </div>
      </section>

      <section className='login-form-panel relative flex h-[100dvh] items-center justify-center overflow-hidden px-5 py-5 sm:px-8 sm:py-8'>
        <div className='absolute right-5 top-5 sm:right-8 sm:top-8'><ThemeToggle label={t('Toggle theme')} /></div>
        <div className='w-full max-w-[460px]'>
          <div className={`login-card${errorMessage ? ' login-card--with-error' : ''} rounded-lg border border-[var(--border-subtle)] bg-[var(--surface-panel)] p-5 shadow-[var(--shadow-popover)] sm:p-8`}>
            <div className='login-card-header text-center'><div className='login-card-header-icon mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-[var(--brand-soft)] text-2xl text-[var(--brand)] sm:mb-5 sm:h-14 sm:w-14'><FiShield aria-hidden='true' /></div><p className='text-sm font-semibold text-[var(--brand)]'>{t('Billing Management')}</p><h2 className='mt-1.5 text-2xl font-bold text-[var(--text-primary)] sm:mt-2'>{t('Welcome back')}</h2><p className='mt-1.5 text-sm leading-6 text-[var(--text-secondary)] sm:mt-2'>{t('Enter your credentials to continue.')}</p></div>
            {errorMessage && <div className='login-card-alert mt-6 rounded-lg border border-red-400/35 bg-red-500/10 px-4 py-3 text-sm text-[var(--danger)]' role='alert'>{errorMessage}</div>}
            <>
              <form className='login-card-form mt-5 space-y-4 sm:mt-7 sm:space-y-5' onSubmit={onSubmit} noValidate>
                <FormField label={t('Email')} required error={fieldErrors.email} errorId='login-email-error'><div className='relative'><FiMail className='pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text-muted)]' aria-hidden='true' /><input name='email' type='email' required autoFocus autoComplete='email' aria-invalid={Boolean(fieldErrors.email)} aria-describedby={fieldErrors.email ? 'login-email-error' : undefined} onBlur={validateField} onChange={clearFieldError} className='login-input h-12 w-full rounded-lg border border-[var(--border-strong)] bg-[var(--surface-panel-muted)] px-4 pl-12 text-base text-[var(--text-primary)] placeholder:text-[var(--text-muted)] sm:h-[52px]' placeholder={t('user@company.com')} /></div></FormField>
                <FormField label={t('Password')} required error={fieldErrors.password} errorId='login-password-error'><div className='relative'><FiLock className='pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text-muted)]' aria-hidden='true' /><input name='password' type={showPassword ? 'text' : 'password'} required autoComplete='current-password' aria-invalid={Boolean(fieldErrors.password)} aria-describedby={fieldErrors.password ? 'login-password-error' : undefined} onBlur={validateField} onChange={clearFieldError} className='login-input h-12 w-full rounded-lg border border-[var(--border-strong)] bg-[var(--surface-panel-muted)] px-4 pl-12 pr-12 text-base text-[var(--text-primary)] placeholder:text-[var(--text-muted)] sm:h-[52px]' placeholder={t('Password')} /><AppTooltip label={showPassword ? t('Hide password') : t('Show password')}><button type='button' onClick={() => setShowPassword((prev) => !prev)} className='absolute right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-lg text-[var(--text-muted)] hover:bg-[var(--surface-raised)] hover:text-[var(--text-primary)]' aria-label={showPassword ? t('Hide password') : t('Show password')}>{showPassword ? <FiEyeOff aria-hidden='true' /> : <FiEye aria-hidden='true' />}</button></AppTooltip></div></FormField>
                <Button type='submit' variant='primary' className='h-12 w-full text-base sm:h-[52px]' loading={isSubmitting}>{isSubmitting ? t('Signing in...') : t('Sign in')}</Button>
              </form>
              <div className='login-card-divider my-4 flex items-center gap-3 sm:my-6' aria-hidden='true'><span className='h-px flex-1 bg-[var(--border-subtle)]' /><span className='text-xs font-medium text-[var(--text-muted)]'>{t('Or continue with')}</span><span className='h-px flex-1 bg-[var(--border-subtle)]' /></div>
              <div><Button type='button' variant='secondary' disabled className='h-12 w-full border-[var(--border-subtle)] text-[var(--text-secondary)] disabled:opacity-100 sm:h-[50px]' aria-describedby='social-login-note'><FcGoogle className='h-5 w-5' aria-hidden='true' />{t('Continue with Google')}</Button><p id='social-login-note' className='mt-2 text-center text-xs text-[var(--text-muted)] sm:mt-3'>{t('Social sign-in available soon')}</p></div>
            </>
          </div>
        </div>
      </section>
    </div>
  );
};
